import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GouvGeorisquesDto, PropertyDto, UserDto } from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { EtxeEnumsNiceLabelUtils } from '../../../../../utils/etxe-enums-nice-label.utils';
import { EtxeEnumDisplayPipe } from '../../../../pipes/etxe-enum-display.pipe';
import { fetchOsmPois, OsmPoi } from '../../../../utils/osm-poi.utils';
import { PropertyPdfPrintUtils, PropertyPrintOptions } from '../../../../utils/property-pdf-print.utils';
import { generateStaticMapDataUrl } from '../../../../utils/static-map.utils';
import { getPropertyEquipmentInfo } from '../../../property-characteristics/equipments-list/property-equipment.constants';
import { NoiseSourceDisplayPipe } from '../../../property-geospatial/cards/noise-card/noise-source-display.pipe';

export interface PropertyPdfExportDialogData {
  property: PropertyDto;
  isOwner?: boolean;
}

@Component({
  selector: 'etxe-property-pdf-export-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    EtxeEnumDisplayPipe,
    NoiseSourceDisplayPipe,
  ],
  templateUrl: './property-pdf-export-dialog.component.html',
  styleUrls: ['./property-pdf-export-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col flex-1 min-h-0 h-full w-full overflow-hidden bg-white',
  },
})
export class PropertyPdfExportDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<PropertyPdfExportDialogComponent>);
  private readonly data = inject<PropertyPdfExportDialogData>(MAT_DIALOG_DATA);
  private readonly userStore = inject(UserStore);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly property = signal<PropertyDto>(this.data.property);
  readonly isOwner = signal<boolean>(this.data.isOwner ?? false);

  // Document format mode
  readonly documentMode = signal<'detailed' | 'summary'>('detailed');
  readonly isDetailed = computed(() => this.documentMode() === 'detailed');

  // Print options
  readonly includeContactInfos = signal<boolean>(true);
  readonly includeExactAddress = signal<boolean>(this.data.isOwner ?? false);
  readonly includeMapAndPois = signal<boolean>(true);
  readonly includeGeospatial = signal<boolean>(true);

  // POIs & Map
  readonly pois = signal<OsmPoi[]>([]);
  readonly loadingPois = signal<boolean>(false);
  readonly staticMapDataUrl = signal<string | null>(null);

  // Géorisques from property
  readonly geohazard = computed<GouvGeorisquesDto | null | undefined>(
    () => this.property().geospatial?.geohazard,
  );

  // Zoom level for print-like preview (default 80%)
  readonly zoomLevel = signal<number>(80);

  zoomIn(): void {
    this.zoomLevel.update((z) => Math.min(130, z + 10));
  }

  zoomOut(): void {
    this.zoomLevel.update((z) => Math.max(50, z - 10));
  }

  resetZoom(): void {
    this.zoomLevel.set(80);
  }

  // Georisques breakdown
  readonly geohazardData = computed(() => PropertyPdfPrintUtils.extractGeohazardRisks(this.geohazard()));
  readonly naturalRisks = computed(() => this.geohazardData().naturalRisks);
  readonly technoRisks = computed(() => this.geohazardData().technoRisks);
  readonly detectedRisksCount = computed(() => this.geohazardData().detectedCount);

  // Formatted date & reference
  readonly currentDate = new Date();
  readonly propertyReference = computed<string>(() => {
    const ref = this.property().definition?.reference?.trim();
    if (ref) return ref;
    const id = this.property()._id?.toString();
    return id ? `#${id.slice(-6).toUpperCase()}` : 'ANNONCE';
  });

  ngOnInit(): void {
    const loc = this.property().location;
    const lat = loc?.coordinates?.latitude;
    const lng = loc?.coordinates?.longitude;

    // Load POIs
    if (loc?.pois && loc.pois.length > 0) {
      this.pois.set(loc.pois as OsmPoi[]);
    } else if (lat && lng) {
      this.loadingPois.set(true);
      fetchOsmPois(lat, lng, 1000)
        .then((result) => this.pois.set(result))
        .catch(() => this.pois.set([]))
        .finally(() => this.loadingPois.set(false));
    }

    // Generate in-memory static map image as data URL
    if (lat && lng) {
      const title = this.property().presentation?.title || 'Emplacement du bien';
      void generateStaticMapDataUrl(lat, lng, { title, width: 750, height: 260 }).then((url) => {
        if (url) {
          this.staticMapDataUrl.set(url);
        }
      });
    }
  }

  // Seller info resolution
  readonly sellerName = computed<string>(() => {
    const prop = this.property();
    const owner = prop.owner;
    if (owner && typeof owner === 'object' && 'identity' in owner) {
      const u = owner as UserDto;
      if (u.identity?.firstName || u.identity?.lastName) {
        return `${u.identity.firstName ?? ''} ${u.identity.lastName ?? ''}`.trim();
      }
    }
    const current = this.userStore.current();
    if (current?.identity?.firstName || current?.identity?.lastName) {
      return `${current.identity.firstName ?? ''} ${current.identity.lastName ?? ''}`.trim();
    }
    return 'Propriétaire vendeur';
  });

  readonly sellerPhone = computed<string | null>(() => {
    const prop = this.property();
    const owner = prop.owner;
    if (owner && typeof owner === 'object' && 'contactInfos' in owner) {
      const u = owner as UserDto;
      if (u.contactInfos?.mobileNumber) return u.contactInfos.mobileNumber;
    }
    const current = this.userStore.current();
    return current?.contactInfos?.mobileNumber ?? null;
  });

  readonly sellerEmail = computed<string | null>(() => {
    const prop = this.property();
    const owner = prop.owner;
    if (owner && typeof owner === 'object' && 'contactInfos' in owner) {
      const u = owner as UserDto;
      if (u.contactInfos?.email) return u.contactInfos.email;
    }
    const current = this.userStore.current();
    return current?.contactInfos?.email ?? null;
  });

  // Presentation & metrics
  readonly photos = computed(() => this.property().media?.photos || []);
  readonly mainPhotoUrl = computed(() => this.photos()[0]?.url || null);
  readonly secondaryPhotos = computed(() => this.photos().slice(1, this.isDetailed() ? 5 : 4));

  readonly priceFormatted = computed(() =>
    PropertyPdfPrintUtils.formatPrice(this.property().financials?.sellingPriceCents),
  );

  readonly pricePerM2 = computed(() => {
    const priceCents = this.property().financials?.sellingPriceCents;
    const surface = this.property().keyInfo?.surface?.habitable;
    if (!priceCents || !surface || surface <= 0) return null;
    return `${Math.round(priceCents / 100 / surface).toLocaleString('fr-FR')} €/m²`;
  });

  readonly mainRoomsCount = computed(() => PropertyPdfPrintUtils.countMainRooms(this.property()));

  readonly bedroomsCount = computed(
    () => (this.property().characteristics?.interior?.bedroom || []).length || null,
  );

  readonly bathroomsCount = computed(() => {
    const interior = this.property().characteristics?.interior;
    if (!interior) return null;
    const b = (interior.bathroom || []).length;
    const s = (interior.showerRoom || []).length;
    return b + s > 0 ? b + s : null;
  });

  readonly wcCount = computed(() => this.property().characteristics?.interior?.wc || null);

  readonly constructionYear = computed(() => {
    const c = this.property().construction;
    if (c?.precision) return c.precision;
    if (c?.year) return EtxeEnumsNiceLabelUtils.constructionPeriod(c.year);
    return null;
  });

  readonly parkingsCount = computed(() => {
    const list = this.property().characteristics?.parkings || [];
    return list.reduce((acc, p) => acc + (p.count || 1), 0) || null;
  });

  // Detailed Rooms list
  readonly detailedRoomsList = computed(() => {
    const interior = this.property().characteristics?.interior;
    const list: Array<{ name: string; surface?: number; precision?: string }> = [];
    if (interior?.bedroom) {
      interior.bedroom.forEach((b, idx) => {
        list.push({
          name: `Chambre ${idx + 1}`,
          surface: b.surface,
          precision: b.precision,
        });
      });
    }
    if (interior?.rooms) {
      interior.rooms.forEach((r) => {
        list.push({
          name: r.type ? EtxeEnumsNiceLabelUtils.room(r.type) : 'Pièce',
          surface: r.surface,
          precision: r.precision,
        });
      });
    }
    if (interior?.bathroom) {
      interior.bathroom.forEach((_, idx) => {
        list.push({ name: `Salle de bain ${idx + 1}` });
      });
    }
    if (interior?.showerRoom) {
      interior.showerRoom.forEach((_, idx) => {
        list.push({ name: `Salle d'eau ${idx + 1}` });
      });
    }
    return list;
  });

  // Construction info
  readonly heatingLabel = computed(() => {
    const h = this.property().dpe?.technical?.heatingType;
    if (!h) return null;
    return EtxeEnumsNiceLabelUtils.heatingType(h as never);
  });

  readonly heatingEnergyLabel = computed(() => {
    const e = this.property().dpe?.technical?.heatingEnergy;
    if (!e) return null;
    return EtxeEnumsNiceLabelUtils.heatingEnergy(e as never);
  });

  readonly wallsMaterial = computed(() => {
    const walls = this.property().construction?.material?.walls;
    if (!walls?.length) return null;
    return walls.map((m) => EtxeEnumsNiceLabelUtils.constructionMaterial(m)).join(', ');
  });

  readonly roofMaterial = computed(() => {
    const roof = this.property().construction?.material?.roof;
    if (!roof?.length) return null;
    return roof.map((r) => EtxeEnumsNiceLabelUtils.roofMaterial(r)).join(', ');
  });

  readonly conditionLabel = computed(() => {
    const cond = this.property().construction?.conditions?.interior;
    if (!cond) return null;
    return EtxeEnumsNiceLabelUtils.condition(cond);
  });

  readonly orientationLabel = computed(() => {
    const ori = this.property().construction?.orientation;
    if (!ori?.length) return null;
    return ori.map((o) => EtxeEnumsNiceLabelUtils.orientation(o)).join(', ');
  });

  readonly gardenSurface = computed(() => {
    const ext = this.property().characteristics?.exterior;
    const s = (ext?.garden || []).reduce((acc, g) => acc + (g.surface || 0), 0);
    return s > 0 ? `${s} m²` : ext?.garden?.length ? 'Oui' : null;
  });

  readonly terraceSurface = computed(() => {
    const ext = this.property().characteristics?.exterior;
    const s = (ext?.terrace || []).reduce((acc, t) => acc + (t.surface || 0), 0);
    return s > 0 ? `${s} m²` : ext?.terrace?.length ? 'Oui' : null;
  });

  readonly balconySurface = computed(() => {
    const ext = this.property().characteristics?.exterior;
    const s = (ext?.balcony || []).reduce((acc, b) => acc + (b.surface || 0), 0);
    return s > 0 ? `${s} m²` : ext?.balcony?.length ? 'Oui' : null;
  });

  readonly propertyTaxFormatted = computed(() => {
    const cents = this.property().financials?.taxesAndFees?.propertyTaxCents;
    if (!cents) return null;
    return `${Math.round(cents / 100).toLocaleString('fr-FR')} €/an`;
  });

  readonly coOwnershipChargesFormatted = computed(() => {
    const cents = this.property().financials?.coOwnershipChargesCents;
    if (!cents) return null;
    return `${Math.round(cents / 100).toLocaleString('fr-FR')} €/mois`;
  });

  // Accessibility & Building
  readonly hasElevator = computed(() => {
    const acc = this.property().keyInfo?.accessibility;
    return acc?.hasElevator ?? acc?.buildingHasElevator ?? null;
  });

  readonly pmrCompliance = computed(() => {
    return this.property().keyInfo?.accessibility?.pmrCompliance ?? null;
  });

  readonly apartmentFloor = computed(() => {
    return this.property().keyInfo?.accessibility?.apartment?.floor ?? null;
  });

  readonly buildingNumberOfFloors = computed(() => {
    return this.property().keyInfo?.accessibility?.buildingNumberOfFloors ?? null;
  });

  // Co-ownership
  readonly isInCoOwnership = computed(() => {
    return this.property().keyInfo?.coOwnership?.isInCoOwnership ?? null;
  });

  readonly numberOfLots = computed(() => {
    return this.property().keyInfo?.coOwnership?.numberOfLots ?? null;
  });

  readonly lotNumber = computed(() => {
    return this.property().keyInfo?.coOwnership?.lotNumber ?? null;
  });

  readonly trusteeName = computed(() => {
    return this.property().keyInfo?.coOwnership?.trustee?.name ?? null;
  });

  // Technical & Comfort
  readonly glazing = computed(() => {
    return (
      this.property().construction?.insulation?.glazing ||
      this.property().dpe?.technical?.glazingQuality ||
      null
    );
  });

  readonly hasAirConditioning = computed(() => {
    return this.property().dpe?.technical?.hasAirConditioning ?? null;
  });

  readonly ventilationType = computed(() => {
    return this.property().dpe?.technical?.ventilationType || null;
  });

  // Geospatial contextual labels
  readonly noiseCategoryLabel = computed(() => {
    const cat = this.property().geospatial?.noise?.category;
    if (!cat) return null;
    return cat === 'calm' ? 'Calme' : cat === 'moderate' ? 'Modéré' : 'Exposé';
  });

  readonly inseeAffluenceLabel = computed(() => {
    const aff = this.property().geospatial?.insee?.affluenceLevel;
    if (!aff) return null;
    return aff === 'very_affluent' ? 'Très aisé' : aff === 'affluent' ? 'Aisé' : 'Médian';
  });

  readonly sitadelPressureLabel = computed(() => {
    const sit = this.property().geospatial?.sitadel?.pressureLevel;
    if (!sit) return null;
    return sit === 'high' ? 'Forte' : sit === 'moderate' ? 'Modérée' : 'Faible';
  });

  // Surface au sol & Availability
  readonly surfaceGround = computed<number | null>(() => this.property().keyInfo?.surface?.ground || null);
  readonly availability = computed(() => this.property().keyInfo?.availability);

  // Mandate details
  readonly mandate = computed(() => this.property().mandate);
  readonly mandatePriorities = computed<string[]>(() => {
    const list = this.mandate()?.priorities || [];
    return list.map((p) => EtxeEnumsNiceLabelUtils.sellerPriority(p));
  });
  readonly mandateKeyManagement = computed<string[]>(() => {
    const list = this.mandate()?.keyManagement || [];
    return list.map((k) => EtxeEnumsNiceLabelUtils.keyManagement(k));
  });

  // Equipments
  readonly equipmentItems = computed(() => {
    const items = this.property().characteristics?.equipments?.items || [];
    return items.map((e) => getPropertyEquipmentInfo(e));
  });

  // Cadastral data helpers
  readonly cadastralParcels = computed(() => this.property().geospatial?.cadastral?.parcels || []);
  readonly primaryParcel = computed(() => this.cadastralParcels()[0] || null);
  readonly cadastralSection = computed<string | null>(() => {
    const p = this.primaryParcel();
    if (p?.section) return p.section;
    if (p?.reference && p.reference.length >= 10) return p.reference.slice(-5, -3);
    return null;
  });
  readonly cadastralNumber = computed<string | null>(() => {
    const p = this.primaryParcel();
    if (p?.numero) return p.numero;
    if (p?.reference && p.reference.length >= 10) return p.reference.slice(-3);
    return null;
  });
  readonly cadastralReference = computed<string | null>(() => this.primaryParcel()?.reference || null);
  readonly cadastralParcelsCount = computed<number>(() => this.cadastralParcels().length);
  readonly hasCadastralData = computed<boolean>(() => {
    const cad = this.property().geospatial?.cadastral;
    return Boolean(
      (cad?.parcels && cad.parcels.length > 0) ||
      cad?.totalSurface ||
      this.property().construction?.groundFootprint ||
      this.sitadelPressureLabel(),
    );
  });

  // Nearest POIs
  readonly nearestPois = computed(() => this.pois().slice(0, 8));

  readonly dpeGrades = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

  getWalkMinutes(meters?: number): number {
    return PropertyPdfPrintUtils.getWalkMinutes(meters);
  }

  getDpeGradeColor(grade: string): string {
    const colors: Record<string, string> = {
      A: '#00803C',
      B: '#54B345',
      C: '#CED932',
      D: '#FFF200',
      E: '#FCBA08',
      F: '#EB8235',
      G: '#D71D20',
    };
    return colors[grade] || '#cbd5e1';
  }

  getGesGradeColor(grade: string): string {
    const colors: Record<string, string> = {
      A: '#8BD1E8',
      B: '#74BBD6',
      C: '#5DA5C4',
      D: '#468FB2',
      E: '#2F79A0',
      F: '#18638E',
      G: '#004D7C',
    };
    return colors[grade] || '#cbd5e1';
  }

  async print(): Promise<void> {
    let mapUrl = this.staticMapDataUrl();
    if (!mapUrl) {
      const loc = this.property().location;
      const lat = loc?.coordinates?.latitude;
      const lng = loc?.coordinates?.longitude;
      if (lat && lng) {
        const title = this.property().presentation?.title || 'Emplacement du bien';
        mapUrl = await generateStaticMapDataUrl(lat, lng, { title, width: 750, height: 260 });
        if (mapUrl) {
          this.staticMapDataUrl.set(mapUrl);
        }
      }
    }

    const options: PropertyPrintOptions = {
      mode: this.documentMode(),
      includeContactInfos: this.includeContactInfos(),
      includeExactAddress: this.includeExactAddress(),
      includeMapAndPois: this.includeMapAndPois(),
      includeGeospatial: this.includeGeospatial(),
      sellerName: this.sellerName(),
      sellerPhone: this.sellerPhone(),
      sellerEmail: this.sellerEmail(),
      pois: this.pois(),
      staticMapUrl: mapUrl,
      geohazard: this.geohazard(),
    };

    PropertyPdfPrintUtils.printPropertySheet(this.property(), options);

    this.alertSnackbar.info({
      title: 'Impression en cours',
      message:
        'La boîte d’impression s’ouvre. Vous pouvez choisir d’imprimer ou « Enregistrer au format PDF ».',
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
